import api from './api';

// ============================================
// Types
// ============================================

export type UserRole = 'ADMIN' | 'MANAGER' | 'SELLER';

export interface UserListItem {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  isSystemUser: boolean;
  createdAt: string;
}

export interface UserDetails extends UserListItem {
  mustChangePassword: boolean;
  updatedAt: string;
  products?: Array<{
    productId: number;
    product: { id: number; name: string; slug: string };
  }>;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  role: UserRole;
  productIds?: number[];
}

export interface UpdateUserRequest {
  name?: string;
  role?: UserRole;
  productIds?: number[];
  active?: boolean;
  isSystemUser?: boolean;
}

export interface CreateUserResponse {
  user: UserDetails;
  temporaryPassword?: string;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  role?: UserRole;
  active?: boolean;
  search?: string;
  includeSystemUsers?: boolean;
}

export interface PaginatedUsersResponse {
  users: UserListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ResetPasswordResponse {
  message: string;
  temporaryPassword: string;
}

// ============================================
// Service
// ============================================

export const usersService = {
  /**
   * List users with pagination and filters
   */
  async list(params: ListUsersParams = {}): Promise<PaginatedUsersResponse> {
    const response = await api.get<PaginatedUsersResponse>('/users', { params });
    return response.data;
  },

  /**
   * Get user by ID
   */
  async getById(id: number): Promise<UserDetails> {
    const response = await api.get<UserDetails>(`/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user
   */
  async create(data: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await api.post<CreateUserResponse>('/users', data);
    return response.data;
  },

  /**
   * Update user details
   */
  async update(id: number, data: UpdateUserRequest): Promise<UserDetails> {
    const response = await api.put<UserDetails>(`/users/${id}`, data);
    return response.data;
  },

  /**
   * Delete (deactivate) a user
   */
  async delete(id: number): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  /**
   * Block a user
   */
  async block(id: number): Promise<void> {
    await api.put(`/users/${id}/block`);
  },

  /**
   * Unblock a user
   */
  async unblock(id: number): Promise<void> {
    await api.put(`/users/${id}/unblock`);
  },

  /**
   * Reset user password
   */
  async resetPassword(id: number): Promise<ResetPasswordResponse> {
    const response = await api.post<ResetPasswordResponse>(`/users/${id}/reset-password`);
    return response.data;
  },
};

export default usersService;
